import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { DollarSign, FileText, Download, ExternalLink, FolderOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PayslipPortalEmbed = ({ impersonatedUser, adminUser }) => {
  const [payslips, setPayslips] = useState([]);
  const [payslipFolderId, setPayslipFolderId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayslipData();
  }, [impersonatedUser]);

  const fetchPayslipData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(
        `${API}/superadmin/impersonate/payslips/${impersonatedUser.employee_id}`,
        config
      );
      
      setPayslips(response.data.payslips || []);
      setPayslipFolderId(response.data.payslip_folder_id || null);
    } catch (error) {
      console.error('Error fetching payslip data:', error);
      toast.error('Unable to fetch payslip data');
    } finally {
      setLoading(false);
    }
  };

  // Group payslips by year
  const payslipsByYear = payslips.reduce((acc, ps) => {
    const year = ps.year || new Date(ps.uploaded_at).getFullYear();
    if (!acc[year]) acc[year] = [];
    acc[year].push(ps);
    return acc;
  }, {});

  const openInDrive = (fileId) => {
    window.open(`https://drive.google.com/file/d/${fileId}/view`, '_blank');
  };

  const openFolder = () => {
    if (payslipFolderId) {
      window.open(`https://drive.google.com/drive/folders/${payslipFolderId}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="payslip-portal-embed">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Payslips for {impersonatedUser.name}
            </span>
            {payslipFolderId && (
              <Button variant="outline" size="sm" onClick={openFolder}>
                <FolderOpen className="w-4 h-4 mr-2" />
                Open Folder in Drive
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {payslips.length === 0 && !payslipFolderId ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No payslips found</p>
              <p className="text-sm mt-1">Payslip folder ID has not been configured for this employee.</p>
            </div>
          ) : payslipFolderId && payslips.length === 0 ? (
            <div className="text-center py-8">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 mb-4">Payslip folder is configured</p>
              <Button onClick={openFolder}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Payslip Folder in Google Drive
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(payslipsByYear)
                .sort(([a], [b]) => b - a)
                .map(([year, yearPayslips]) => (
                  <div key={year}>
                    <h3 className="font-semibold text-gray-700 mb-3 border-b pb-2">{year}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {yearPayslips.map(payslip => (
                        <Card key={payslip.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium">{payslip.month}</span>
                              <FileText className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="flex-1 text-xs"
                                onClick={() => openInDrive(payslip.file_id)}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                View
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayslipPortalEmbed;
